import { type EnhancedProductData } from '@/features/cms/components/CMS/types'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { screenSizes } from '@shipt/design-system-themes'
import { Headline, Body } from '@shipt/design-system-typography'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { staticColors } from '@/features/shared/theme/staticColors'
import { ViewAllLink } from '@/features/cms/components/CMS/components/Shelves/ViewAllLink'
import { CMSShelfLayoutContainer } from '@/features/cms/components/CMS/components/Shelves/CMSShelfLayoutContainer'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { classNames } from '@/features/shared/utils/classNames'
import { ChunkedProductsShelfRenderer } from '@/features/cms/components/CMS/components/Shelves/ChunkedProductsShelfRenderer'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useCallback } from 'react'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { NextLink } from '@/features/shared/elements/Link'
import { getShelfViewAll } from '@/features/cms/components/CMS/components/Shelves/getShelfViewAll'

type Props = EnhancedProductData & { index: number }

/**
 * This component corresponds to the `enhanced_product` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/enhanced_product
 * - **Production** - https://cms.shipt.com/content-type/pool/enhanced_product
 */
export const EnhancedProduct = ({
  content_type_id,
  id,
  data,
  name,
  index: shelfIndex,
  analytic_slug,
}: Props) => {
  const { image_placement, image, view_all_link, heading, subheading, items } = data

  const normalizedProducts = items?.map((item) => normalizeProduct(item)) ?? []
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const handleTrackElementClicked = useCallback(() => {
    trackCMSElementClicked({
      type: 'link',
      content: 'View all',
      // For the shelf-related CMS components, the DS team would like for us to continue sending the shelf's heading/title.
      shelf_name: heading,
      shelf_index: shelfIndex,
      display_sublocation: heading,
    })
  }, [heading, shelfIndex, trackCMSElementClicked])

  const viewAll = getShelfViewAll({
    viewAllHref: view_all_link?.url,
    onViewAll: handleTrackElementClicked,
  })
  const viewAllLabel = `View All ${name}`

  const shelfImage = (
    <Image
      src={image.src}
      alt=""
      fill
      sizes="500px"
      style={{ objectFit: 'cover', objectPosition: 'center' }}
    />
  )

  return (
    <CMSShelfLayoutContainerEnhanced {...getCMSWrapperProps({ content_type_id, id })}>
      <EnhancedShelfWrapper
        className={classNames(
          image_placement.startsWith('Desktop: Right') && 'alt-desktop-image',
          image_placement.endsWith('Mobile: Bottom') && 'alt-mobile-image',
        )}
      >
        <SkeletonConsumer height={408} width="50%">
          {viewAll.viewAllHref ? (
            <EnhancedShelfImageButton
              href={viewAll.viewAllHref}
              onClick={viewAll.onViewAll}
              aria-label={viewAllLabel}
            >
              {shelfImage}
            </EnhancedShelfImageButton>
          ) : (
            <EnhancedShelfImageWrapper aria-hidden>{shelfImage}</EnhancedShelfImageWrapper>
          )}
        </SkeletonConsumer>

        <ContentContainer>
          <HeadingContainer>
            {!!data.heading && (
              <SkeletonTitle>
                <Heading as="h2" variant="primary">
                  {heading}
                </Heading>
              </SkeletonTitle>
            )}

            <DescriptionWrapper>
              <DescriptionText as="span" variant="secondary">
                {subheading}&nbsp;
              </DescriptionText>
              <ViewAllLink
                link={view_all_link}
                onClick={handleTrackElementClicked}
                label={viewAllLabel}
              />
            </DescriptionWrapper>
          </HeadingContainer>
          <ContentWrapper>
            <ChunkedProductsShelfRenderer
              shelfIndex={shelfIndex}
              shelfName={name}
              products={normalizedProducts}
              viewAll={viewAll}
              analyticSlug={analytic_slug}
              linkToUniversalPDP={false}
            />
          </ContentWrapper>
        </ContentContainer>
      </EnhancedShelfWrapper>
    </CMSShelfLayoutContainerEnhanced>
  )
}

const CMSShelfLayoutContainerEnhanced = styled(CMSShelfLayoutContainer)`
  width: 100%;
`

const EnhancedShelfWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;

  @media ${screenSizes.max_mobile} {
    &.alt-mobile-image {
      flex-direction: column-reverse;
    }
  }

  @media ${screenSizes.tablet} {
    border-radius: 8px;
    margin-bottom: 2rem;
    flex-direction: row;
    box-shadow: 0 0.125rem 0.5rem rgb(24 12 32 / 20%);

    &.alt-desktop-image {
      flex-direction: row-reverse;
    }
  }
`

const ShelfImageWrapperStyle = css`
  width: 100%;
  min-height: 14.25rem;
  position: relative;

  @media ${screenSizes.tablet} {
    width: 50%;
    border-radius: 0;
  }

  @media ${screenSizes.max_mobile} {
    padding: 16px 0 0 16px;
  }
`

const EnhancedShelfImageButton = styled(NextLink)`
  ${ShelfImageWrapperStyle};
`

const EnhancedShelfImageWrapper = styled.div`
  ${ShelfImageWrapperStyle};
`

const ContentContainer = styled.div`
  width: 100%;
  padding: 32px 32px 0;

  @media ${screenSizes.tablet} {
    width: 50%;
  }

  @media ${screenSizes.max_mobile} {
    padding: 16px 0 0 16px;
  }
`

const ContentWrapper = styled.div`
  & button[aria-label='Next items'] {
    margin-right: 3rem;
  }

  & button[aria-label='Previous items'] {
    margin-left: 3rem;
  }
`

const HeadingContainer = styled.div`
  margin-bottom: 1.5rem;
`

const Heading = styled(Headline)`
  color: ${staticColors.gray900};
`

const DescriptionWrapper = styled.div`
  display: flex;

  @media ${screenSizes.tablet} {
    padding: 0;
  }
`

const DescriptionText = styled(Body)`
  margin-right: 0.5rem;
`

const SkeletonTitle = styled(SkeletonConsumer)`
  height: 2rem;
  width: 18.75rem;
  margin-bottom: 1rem;
  margin-right: auto;
  margin-left: auto;
`
